/**
 * android-install-banner.ts — Android native-app install prompt for the invite
 * lander (ENG-2068, the Android counterpart of smart-banner.ts).
 *
 * The web app manifest declares the related Play identity for browsers that
 * support native-app install signals. The visible prompt is also rendered in
 * page because browser install events are conditional and do not provide the
 * deterministic first-visit handoff that Apple's metadata does. Like the Apple
 * banner, this prompt's job is store handoff — a device with the app installed
 * normally opens the App Link directly and never reaches this page.
 *
 * The Android application id is derived from the per-environment Play Store page
 * URL the deploying stack already injects (`VITE_ANDROID_PLAY_STORE_URL`, one
 * Play record per app variant — infra/lib/environments.ts MOBILE_APP_IDENTITIES,
 * prod `health.suvi.app`), so each environment's lander offers its own identity
 * and no second identifier is plumbed through the build.
 *
 * The manifest must be a real fetchable, installable manifest linked from the
 * HTML at parse time, so vite.config.ts emits `manifest.json` and the
 * `<link rel="manifest">` tag statically at build time. The in-page prompt is
 * Android-only, dismissible for the current browser tab, and activation-gated
 * per environment. Every current environment is off pending enrolled Play-device
 * proof, and the Apple banner's care-dashboard hostname exclusion remains an
 * independent deny even after activation.
 */

import { isNativeAppPromotionEligibleHost } from "./app-install-host.ts";
import { detectStorePlatform } from "./invite-link.ts";

/** Android application id (reverse-DNS package name) grammar. */
const ANDROID_APPLICATION_ID_RE =
  /^[A-Za-z][A-Za-z0-9_]*(?:\.[A-Za-z][A-Za-z0-9_]*)+$/;
const PLAY_STORE_ORIGIN = "https://play.google.com";
const PLAY_STORE_APP_PATH_RE = /^\/store\/apps\/details\/?$/;

export const ANDROID_INSTALL_BANNER_DISMISSED_KEY =
  "suvi:android-install-banner-dismissed";

export interface AndroidInstallBannerStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export interface AndroidInstallBannerView {
  setHidden(hidden: boolean): void;
  setStoreUrl(url: string): void;
  onDismiss(listener: () => void): void;
}

export interface AndroidInstallBannerContext {
  /** Must be explicitly true; missing and every other value fail closed. */
  enabled?: boolean;
  androidPlayStoreUrl: string;
  hostname: string;
  userAgent: string;
  maxTouchPoints: number;
  storage?: AndroidInstallBannerStorage;
}

/**
 * The Android application id from a Play Store page URL
 * (`https://play.google.com/store/apps/details?id=health.suvi.app` →
 * `"health.suvi.app"`), or null when the URL is unparseable or its `id` query
 * parameter is not a well-formed application id. Lookalike hosts, non-HTTPS
 * URLs, and unrelated Play paths are rejected rather than trusted for manifest
 * generation.
 */
export function playStoreApplicationId(androidPlayStoreUrl: string): string | null {
  let parsed: URL;
  try {
    parsed = new URL(androidPlayStoreUrl.trim());
  } catch {
    return null;
  }
  if (
    parsed.origin !== PLAY_STORE_ORIGIN ||
    parsed.username !== "" ||
    parsed.password !== "" ||
    !PLAY_STORE_APP_PATH_RE.test(parsed.pathname)
  ) {
    return null;
  }
  const applicationId = parsed.searchParams.get("id") ?? "";
  return ANDROID_APPLICATION_ID_RE.test(applicationId) ? applicationId : null;
}

/** Only the exact source-controlled build value `true` activates the feature. */
export function isAndroidInstallBannerActivationEnabled(
  value: string | undefined,
): boolean {
  return value?.trim() === "true";
}

/**
 * The web app manifest content for one environment's app identity, or null when
 * no application id can be derived (the manifest is omitted rather than emitted
 * malformed). Chrome requires the name, launch behavior, and suitably sized
 * icons even when the preferred install target is the related native app.
 */
export function androidInstallManifest(
  androidPlayStoreUrl: string,
  deploymentHostname: string,
  activationEnabled: boolean,
) {
  const applicationId = playStoreApplicationId(androidPlayStoreUrl);
  if (
    activationEnabled !== true ||
    applicationId === null ||
    !isNativeAppPromotionEligibleHost(deploymentHostname)
  ) {
    return null;
  }
  return {
    name: "Suvi Health",
    short_name: "Suvi",
    start_url: "/invite/",
    display: "standalone",
    background_color: "#ffffff",
    theme_color: "#925878",
    icons: [
      {
        src: "/pwa-192x192.png",
        sizes: "192x192",
        type: "image/png",
        purpose: "any",
      },
      {
        src: "/pwa-512x512.png",
        sizes: "512x512",
        type: "image/png",
        purpose: "any",
      },
      {
        src: "/pwa-maskable-512x512.png",
        sizes: "512x512",
        type: "image/png",
        purpose: "maskable",
      },
    ],
    prefer_related_applications: true,
    related_applications: [
      {
        platform: "play",
        id: applicationId,
        url: androidPlayStoreUrl.trim(),
      },
    ],
  };
}

function wasDismissed(storage: AndroidInstallBannerStorage | undefined): boolean {
  if (!storage) return false;
  try {
    return storage.getItem(ANDROID_INSTALL_BANNER_DISMISSED_KEY) === "1";
  } catch {
    return false;
  }
}

function rememberDismissal(storage: AndroidInstallBannerStorage | undefined): void {
  if (!storage) return;
  try {
    storage.setItem(ANDROID_INSTALL_BANNER_DISMISSED_KEY, "1");
  } catch {
    // Storage can be unavailable in privacy modes; hiding for this page still works.
  }
}

/**
 * Return the exact Play listing to promote, or null when this browser/host is
 * ineligible, the listing is malformed, or this tab already dismissed it.
 */
export function androidInstallBannerUrl(
  context: AndroidInstallBannerContext,
): string | null {
  if (
    context.enabled !== true ||
    detectStorePlatform(context.userAgent, context.maxTouchPoints) !== "android" ||
    !isNativeAppPromotionEligibleHost(context.hostname) ||
    wasDismissed(context.storage) ||
    playStoreApplicationId(context.androidPlayStoreUrl) === null
  ) {
    return null;
  }
  return context.androidPlayStoreUrl.trim();
}

/** Wire the small prompt without coupling its behavior tests to a DOM emulator. */
export function initializeAndroidInstallBanner(
  context: AndroidInstallBannerContext,
  view: AndroidInstallBannerView,
): void {
  const storeUrl = androidInstallBannerUrl(context);
  if (storeUrl === null) {
    view.setHidden(true);
    return;
  }

  view.setStoreUrl(storeUrl);
  view.onDismiss(() => {
    rememberDismissal(context.storage);
    view.setHidden(true);
  });
  view.setHidden(false);
}

/**
 * Bind the tested controller to the actual static banner markup. Missing or
 * malformed markup fails closed without interrupting invite redemption.
 */
export function setupAndroidInstallBanner(
  context: AndroidInstallBannerContext,
  documentRoot: Document,
): void {
  const banner = documentRoot.getElementById("android-install-banner");
  const storeLink = documentRoot.getElementById("android-install-link");
  const dismissButton = documentRoot.getElementById("android-install-dismiss");
  if (banner instanceof HTMLElement) banner.hidden = true;
  if (
    !(banner instanceof HTMLElement) ||
    !(storeLink instanceof HTMLAnchorElement) ||
    !(dismissButton instanceof HTMLButtonElement)
  ) {
    return;
  }

  initializeAndroidInstallBanner(context, {
    setHidden: (hidden) => {
      banner.hidden = hidden;
    },
    setStoreUrl: (url) => {
      storeLink.href = url;
    },
    onDismiss: (listener) => {
      dismissButton.addEventListener("click", listener, { once: true });
    },
  });
}
